import React, {
  memo, useMemo, useEffect, useRef, useContext, useCallback
} from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { provideHooks } from 'redial';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import CountryLandingSection from 'components/CountryLandingSection/Loadable';
import ValuesBanner from 'components/ValuesBanner/Loadable';
import FullWidthInformationBanner from 'components/FullWidthInformationBanner/FullWidthInformationBanner';
import CountryOneWaySection from 'components/CountryOneWaySection/Loadable';
import PageSectionHeaders from 'components/PageSectionHeaders/Loadable';
import CityCard from 'components/CityCard/Loadable';
// import ItineraryCardResolver from 'components/ItineraryCard/WeakLoadable';
import ItineraryCard from 'components/ItineraryCard/Loadable';
import { getPriceQuote } from 'components/PriceNumber/PriceNumber';
import LandingSearchFeatures from 'components/LandingSearchFeatures/Loadable';
import ButtonWithRightBracket from 'components/ButtonWithRightBracket/Loadable';
import StyledButton from 'components/MuiComponents/RoundedButton';
import { MobileScrollableWrap, MobileScrollableItem } from 'components/ReviewScrollBarHorizontal/ReviewScrollBarHorizontal.styled';
import UserReviews from 'components/ReviewScrollBarHorizontal/ReviewScrollBarHorizontal';
import UserReviewCard from 'components/UserReviewCard/Loadable';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import CountryContext from 'context/CountryContext';
// import OptimizeContext from 'context/OptimizeContext';
import upperFirst from 'lodash.upperfirst';
import isEmpty from 'lodash.isempty';
// import { sendEvent, activateOptimize } from 'utils/gtag';
import { sendEvent } from 'utils/gtag';
import replaceS3WithCDN from 'utils/replaceS3WithCDN';
import config from 'config';
import { mediaMUI, media } from 'theme/styled-theme';

/* -------------------------------------------------------------------------- */
/*                              Reducer & Actions                             */
/* -------------------------------------------------------------------------- */
import countriesReducer, * as countriesActions from 'redux/modules/countries';

const { isCountryLoaded, loadCountry } = countriesActions;

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */
const ONE_WAY_TRIP_ANCHOR = 'one-way-trip';
const AIRPORT_TRANSPORT_ANCHOR = 'airport-transport';

/* -------------------------------------------------------------------------- */
/*                              styled-components                             */
/* -------------------------------------------------------------------------- */

const StyledMobileScrollableItem = styled(MobileScrollableItem)`
  max-width: 278px;
`;

const ItineraryWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -0.5rem;
`;

const StyledItineraryCard = styled.div`
  flex: 1 0 25%;
  padding: 0 0.5rem 1rem;
  max-width: 25%;

  ${media.forPhoneOnly`
    flex: 1 0 50%;
    max-width: 50%;
  `}
`;

const FullWidthInformationBannerWrap = styled.div`
  margin-top: 48px;
`;

/* -------------------------------------------------------------------------- */
/*                        Rendering Function Components                       */
/* -------------------------------------------------------------------------- */
// eslint-disable-next-line react/prop-types
const Cities = memo(({ cities = [], country: countryName }) => (
  <MobileScrollableWrap key="Cities Scrollable">
    {
      cities.map((city, index) => {
        const {
          cover_photo_thumbnail_url: _photo,
          name,
          country,
          overall_rating: overallRating,
        } = city;
        const citiesCountryName = country?.name;
        const currency = country?.currency;
        const photo = replaceS3WithCDN(_photo);
        if (citiesCountryName !== countryName) return null;

        const data = {
          name,
          photos: [photo],
          currency: currency.toUpperCase(),
          overallRating,
        };

        return (
          <StyledMobileScrollableItem key={`city-${index}`}>
            <Link
              to={`/search/${name}`}
              onClick={() => {
                sendEvent({ action: 'Click City Card on Country', category: 'navigation', label: name });
              }}
            >
              <CityCard
                city={data}
              />
            </Link>
          </StyledMobileScrollableItem>
        );
      })
    }
  </MobileScrollableWrap>
));

// eslint-disable-next-line react/prop-types
const Itineraries = memo(({ itineraries = [], country, currency }) => {
  // const ItineraryCard = ItineraryCardResolver();

  return (
    <ItineraryWrap>
      {
        itineraries.map((itinerary, index) => {
          const {
            name,
            photo: { image_url: _photo },
            multiple_days: multipleDays,
            duration: totalHours,
            city: { name: city },
            bookings_count: bookings,
            price_per_person_minimum: price,
            profit_margin: margin,
            slug,
          } = itinerary;
          const days = multipleDays ? totalHours : 1;
          const priceFrom = getPriceQuote({ price, margin });
          const photo = replaceS3WithCDN(_photo);
          const data = {
            name,
            totalHours,
            days,
            priceFrom,
            city,
            country,
            bookings,
            photo,
            currency
          };

          return (
            <StyledItineraryCard key={`itinerary-${index}`}>
              <Link
                to={`/itinerary/${slug}`}
                onClick={() => {
                  sendEvent({ action: 'Click Itinerary Card on Country', category: 'navigation', label: name });
                }}
              >
                <ItineraryCard {...data} />
              </Link>
            </StyledItineraryCard>
          );
        })
      }
    </ItineraryWrap>
  );
});

const OverflowHidden = styled.div`
  overflow: hidden;
`;

// eslint-disable-next-line react/prop-types
const RenderHelmet = memo(({ t, countryName: _countryName, coverPhoto }) => {
  const countryName = upperFirst(_countryName);
  const pathname = __SERVER__
    ? `${config.app.url}/country/${_countryName}`
    : `${config.app.url}${window.location.pathname}`;
  return (
    <>
      <Helmet
        title={t('meta:META.COUNTRY.TITLE', {
          countryName,
        })}
      />
      <Helmet
        link={[
          {
            rel: 'canonical',
            href: pathname
          }
        ]}
      />
      <Helmet
        meta={[
          {
            name: 'description',
            content: t('meta:META.COUNTRY.DESCRIPTION', {
              countryName,
            })
          },
          { id: 'english-canonical-url', content: '' },
          { property: 'og:url', content: pathname },
          {
            property: 'og:title',
            content: t('meta:META.COUNTRY.TITLE', {
              countryName,
            })
          },
          {
            property: 'og:description',
            content: t('meta:META.COUNTRY.DESCRIPTION', {
              countryName,
            })
          },
          {
            property: 'og:image',
            content: coverPhoto,
          }
        ]}
      />
    </>
  );
});

// eslint-disable-next-line react/prop-types
const BreadcrumbStructuredData = memo(({ countryName }) => (
  <script
    type="application/ld+json"
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `{
        "@context": "http://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@id": "https://www.ownrides.com",
            "name": "OWNRIDES"
          }
        },{
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@id": "https://www.ownrides.com/country/${countryName}",
            "name": "${upperFirst(countryName)}"
          }
        }]
      }`
    }}
  />
));

/* -------------------------------------------------------------------------- */
/*                               Main Component                               */
/* -------------------------------------------------------------------------- */
const Country = props => {
  const {
    cities,
    location,
    match,
    history,
    countries = {},
    countriesLoaded,
    countriesLoadedError
  } = props;
  const { t } = useTranslation(['new-version', 'meta']);
  const countryName = match?.params?.name;
  const country = countries[countryName] || {};
  const airportRef = useRef();

  useEffect(() => {
    const { hash } = location;
    const hashPosition = hash.slice(1);

    // Anchor scrolling for specific anchor
    if (hashPosition === ONE_WAY_TRIP_ANCHOR || hashPosition === AIRPORT_TRANSPORT_ANCHOR) {
      document.addEventListener('DOMContentLoaded', () => {
        const { top } = airportRef.current.getBoundingClientRect();
        window.scroll({
          top: window.scrollY + top - 80,
          behavior: 'smooth',
        });
      });
    }
  }, []);

  const {
    itineraries,
    cover_photo_url: _coverPhoto,
    reviews,
    currency,
  } = country;
  const coverPhoto = useMemo(() => replaceS3WithCDN(_coverPhoto), [_coverPhoto]);
  const infoTitle = t('COMMON.KEY_VALUE.TITLE');
  const infoDescription = t('COMMON.KEY_VALUE.DESCRIPTION');
  const matchePhone = useMediaQuery(mediaMUI.forPhoneOnly);
  const buttonStyles = useMemo(() => ({
    root: {
      display: matchePhone ? 'flex' : 'none',
      width: '100%',
      margin: '1.5rem 0 3rem',
      color: '#00b33b',
      border: '1px solid #b4b4b4',
    }
  }), [matchePhone]);

  const onDesktopAllTourButtonClick = useCallback(e => {
    e.stopPropagation();
    sendEvent({ action: 'Click All Tours Action Button on Country', category: 'navigation', label: `${countryName} - desktop` });
  }, [countryName]);

  const onMobileAllTourButtonClick = useCallback(e => {
    e.stopPropagation();
    sendEvent({ action: 'Click All Tours Action Button on Country', category: 'navigation', label: `${countryName} - mobile` });
  }, [countryName]);

  const onDesktopAllReviewButtonClick = useCallback(e => {
    e.stopPropagation();
    sendEvent({ action: 'Click Reviews Action Button on Country', category: 'navigation', label: 'desktop' });
  }, []);

  const onMobileAllReviewButtonClick = useCallback(e => {
    e.stopPropagation();
    sendEvent({ action: 'Click Reviews Action Button on Country', category: 'navigation', label: 'mobile' });
  }, []);

  const renderCard = useCallback(data => <UserReviewCard {...data} />, []);

  useEffect(() => {
    if (countriesLoaded && isEmpty(country) && countriesLoadedError) history.replace('/404');
  }, [countriesLoaded, country], countriesLoadedError);

  return (
    <OverflowHidden>
      <RenderHelmet
        t={t}
        countryName={countryName}
        coverPhoto={coverPhoto}
      />
      <BreadcrumbStructuredData countryName={countryName} />
      <CountryLandingSection
        countryName={countryName}
        backgroundPhoto={coverPhoto}
      />

      <ValuesBanner />

      <div className="container">
        <PageSectionHeaders title={t('COUNTRY.DESTINATIONS.HEADING')} />

        <Cities cities={cities} country={countryName} />

        <PageSectionHeaders
          title={t('COUNTRY.TOURS.HEADING')}
          subtitle={t('COUNTRY.TOURS.SUBHEADING')}
          actionButtonEl={(
            <Link
              to={`/search?country=${countryName}`}
              onClick={onDesktopAllTourButtonClick}
              rel="nofollow"
            >
              <ButtonWithRightBracket key="See All Tour Button">
                { t('COMMON.TOURS.ACTION_BUTTON', { name: upperFirst(countryName) }) }
              </ButtonWithRightBracket>
            </Link>
          )}
        />

        <Itineraries
          itineraries={itineraries}
          country={countryName}
          currency={currency}
        />

        <Link
          to={`/search?country=${countryName}`}
          onClick={onMobileAllTourButtonClick}
          rel="nofollow"
        >
          <StyledButton
            key="All Tours Action Button"
            variant="outlined"
            overwritestyles={buttonStyles}
            fullWidth
          >
            { t('COMMON.TOURS.ACTION_BUTTON', { name: upperFirst(countryName) }) }
          </StyledButton>
        </Link>

        <FullWidthInformationBannerWrap>
          <FullWidthInformationBanner
            key={infoTitle}
            title={infoTitle}
            description={infoDescription}
            reverse
            backgroundColor="#26ae8b"
          />
        </FullWidthInformationBannerWrap>

        <PageSectionHeaders
          key={t('COUNTRY.REVIEWS.HEADING')}
          title={t('COUNTRY.REVIEWS.HEADING')}
          subtitle={t('COUNTRY.REVIEWS.SUBHEADING')}
          center
          actionButtonEl={(
            <Link to="/drivers/reviews" onClick={onDesktopAllReviewButtonClick}>
              <ButtonWithRightBracket key="See Reviews Action Button">
                {t('COMMON.REVIEWS.ACTION_BUTTON')}
              </ButtonWithRightBracket>
            </Link>
          )}
        />

        <UserReviews
          reviews={reviews}
          renderCard={renderCard}
        />

        <Link to="/drivers/reviews" onClick={onMobileAllReviewButtonClick}>
          <StyledButton
            variant="outlined"
            overwritestyles={buttonStyles}
            fullWidth
          >
            {t('COMMON.REVIEWS.ACTION_BUTTON')}
          </StyledButton>
        </Link>
      </div>

      <FullWidthInformationBannerWrap>
        <div ref={airportRef} key="airport-transport" />
        <CountryOneWaySection>
          {
            /*
               This is not a pure component,
               due to the launch deadline, I import directly first
               Need refactor
            */
          }
          <CountryContext.Provider value={country}>
            <LandingSearchFeatures />
          </CountryContext.Provider>
        </CountryOneWaySection>
      </FullWidthInformationBannerWrap>
    </OverflowHidden>
  );
};

Country.propTypes = {
  cities: PropTypes.arrayOf(PropTypes.any),
  location: PropTypes.objectOf(PropTypes.any),
  match: PropTypes.objectOf(PropTypes.any),
  history: PropTypes.objectOf(PropTypes.any),
  countries: PropTypes.objectOf(PropTypes.any),
  countriesLoaded: PropTypes.bool,
  countriesLoadedError: PropTypes.oneOfType([PropTypes.string, PropTypes.objectOf(PropTypes.any)]),
};

Country.defaultProps = {
  cities: [],
  location: {},
  match: {},
  history: {},
  countries: {},
  countriesLoaded: false,
  countriesLoadedError: '',
};

export default compose(
  provideHooks({
    inject: ({ store }) => store.inject({
      countries: countriesReducer,
    }),
    fetch: async ({ store: { dispatch, getState }, params }) => {
      const countryName = params.name.toLowerCase();

      if (!isCountryLoaded(getState(), countryName)) {
        await dispatch(loadCountry(countryName)).catch(() => null);
      }
    }
  }),
  connect(
    state => ({
      countries: state.countries?.countries,
      countriesLoaded: state.countries?.loaded,
      countriesLoadedError: state.countries?.error,
      cities: state.cities && state.cities.cities
    }),
  ),
  withRouter
)(Country);
